import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {ObjectIdentifier} from '../../../../../core/shared/basic/object-identifier';
import {UfFlowService} from '../../../../../feature-core/uf/service/api/uf-flow/uf-flow.service';
import {UfFlowTypeService} from '../../../../../feature-core/uf/service/api/uf-flow-type/uf-flow-type.service';
import {UfFlowStartRequestDto} from '../../../../../feature-core/uf/model/dto/api/uf-flow-start-request.dto';
import {UfFlowDto} from '../../../../../feature-core/uf/model/dto/entity/uf-flow.dto';
import {UfFlowTypeDto} from '../../../../../feature-core/uf/model/dto/entity/uf-flow-type.dto';

/**
 * Alias of the uf flow type that creates a NEW XxPerson together with all three
 * side-cars (#3 internal / #4 external-flat / #5 external-typed) in ONE submit.
 * The matching UfFlowType is created by the backend seed; its
 * {@code startObjectType} is `xx.XxPerson`, so it is discovered via
 * {@link UfFlowTypeService#listFlowTypesForObjectType} on that object type.
 *
 * @ticket TM-1000001960
 */
const XX_PLAYGROUND_FLOW_TYPE_ALIAS = 'xx.playground.create.person';

/**
 * Object-type alias the flow type is registered against ({@code startObjectType}
 * of the flow). Used ONLY to discover the flow type by alias — NOT for the start
 * payload.
 *
 * @ticket TM-1000001960
 */
const XX_PERSON_OBJECT_TYPE_ALIAS = 'xx.XxPerson';

/**
 * "uf-Erfassung" tab of the unified xx Side-Car Playground (TM-1000001960).
 *
 * This tab runs the REAL one-form CREATE flow: a single submit creates a new
 * `xx.XxPerson` (firstName / lastName) together with all three side-cars
 * (#3 internal, #4 external-flat, #5 external-typed). The user does NOT pick a
 * person — the flow CREATES it. The form is rendered entirely by `<mvs-uf-flow>`.
 *
 * Lifecycle:
 * 1. resolves the flow type id by alias `xx.playground.create.person` via
 *    {@link UfFlowTypeService#listFlowTypesForObjectType} on `xx.XxPerson`;
 * 2. starts the flow ANONYMOUSLY via {@link UfFlowService#start} (no start
 *    object — the flow CREATES the person);
 * 3. renders the running flow with `<mvs-uf-flow>` (embedded, not full-page);
 * 4. on completion / cancellation resets so another person can be created.
 *
 * When the seed has not yet created the flow type the tab shows a German hint
 * instead of crashing. On a start error a German error message is shown rather
 * than crashing.
 *
 * @ticket TM-1000001960
 */
@Component({
    selector: 'mvs-xx-playground-uf',
    templateUrl: './xx-playground-uf.component.html',
    styleUrls: ['./xx-playground-uf.component.scss'],
    standalone: false
})
export class XxPlaygroundUfComponent implements OnInit, OnDestroy {

    /** True once the flow-type lookup has finished. */
    initialized = false;

    /** True while the initial flow-type resolution is running. */
    loading = false;

    /** German error message for the load / start step, or null when none. */
    errorMessage: string | null = null;

    /**
     * Resolved flow type id for alias `xx.playground.create.person`, or null when
     * the seed has not created it yet (drives the "not yet seeded" hint).
     */
    flowTypeId: number | null = null;

    /** True while the "uf-Flow starten" call is running. */
    starting = false;

    /**
     * Object identifier for the started flow (`uf.UfFlow`, flowId). Bound to the
     * `<mvs-uf-flow>` `objectIdentifier` input; null until a flow is started.
     */
    ufFlowObjectIdentifier: ObjectIdentifier | null = null;

    /** Active subscriptions, torn down on destroy. */
    private subscriptions: Subscription[] = [];

    constructor(
        private flowService: UfFlowService,
        private flowTypeService: UfFlowTypeService
    ) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize Component.
     *
     * @ticket TM-1000001960
     */
    initComponent(): void {
        this.refreshComponent();
    }

    /**
     * Refresh Component: (re)resolve the create-person flow type.
     *
     * @ticket TM-1000001960
     */
    refreshComponent(): void {
        this.resolveFlowType();
    }

    /**
     * Destroy component: unsubscribe from all running subscriptions.
     *
     * @ticket TM-1000001960
     */
    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
        this.subscriptions = [];
    }

    /**
     * Resolve the flow type id for alias `xx.playground.create.person` from the
     * flow types registered for the `xx.XxPerson` start object type (the flow's
     * {@code startObjectType}). Leaves {@link flowTypeId} null when the seed has
     * not created it yet (the template then shows the "not yet seeded" hint).
     *
     * @ticket TM-1000001960
     */
    private resolveFlowType(): void {
        this.loading = true;
        this.errorMessage = null;

        const sub = this.flowTypeService.listFlowTypesForObjectType(XX_PERSON_OBJECT_TYPE_ALIAS).subscribe({
            next: (list) => {
                const entries = (list?.entries ?? []) as UfFlowTypeDto[];
                const match = entries.find(entry => entry.alias === XX_PLAYGROUND_FLOW_TYPE_ALIAS);
                this.flowTypeId = match ? match.id : null;
                this.loading = false;
                this.initialized = true;
            },
            error: () => {
                this.flowTypeId = null;
                this.loading = false;
                this.initialized = true;
                this.errorMessage = 'Der uf-Flow-Typ konnte nicht ermittelt werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    /**
     * Start the one-form CREATE flow and bind the resulting flow id to the
     * `<mvs-uf-flow>` runtime via {@link ufFlowObjectIdentifier}.
     *
     * The flow is started ANONYMOUSLY (no start object): this flow CREATES the
     * person, and the backend supports a null start object — so the start request
     * carries only the flow type id.
     *
     * @ticket TM-1000001960
     */
    startFlow(): void {
        if (this.flowTypeId == null) {
            this.errorMessage = 'uf-Flow noch nicht geseedet — bitte später erneut versuchen.';
            return;
        }

        this.starting = true;
        this.errorMessage = null;
        this.ufFlowObjectIdentifier = null;

        // Anonymous start: this flow CREATES the person, so there is no
        // pre-existing start object. The backend supports a null start object
        // (no neutral-object workaround needed). @ticket TM-1000001960
        const request: UfFlowStartRequestDto = {
            flowTypeId: this.flowTypeId
        };

        const sub = this.flowService.start(request).subscribe({
            next: (flow: UfFlowDto) => {
                this.starting = false;
                if (flow?.id != null) {
                    this.ufFlowObjectIdentifier = new ObjectIdentifier('uf.UfFlow', flow.id);
                } else {
                    this.errorMessage = 'Der uf-Flow konnte nicht gestartet werden.';
                }
            },
            error: () => {
                this.starting = false;
                this.errorMessage = 'Der uf-Flow konnte nicht gestartet werden.';
            }
        });
        this.subscriptions.push(sub);
    }

    /**
     * Flow completed: clear the running flow so the user can create another
     * person. The created person and its side-cars are now visible in the other
     * tabs.
     *
     * @ticket TM-1000001960
     */
    onFlowComplete(_flow: UfFlowDto): void {
        this.ufFlowObjectIdentifier = null;
    }

    /**
     * Flow cancelled: clear the running flow and return to the start view.
     *
     * @ticket TM-1000001960
     */
    onFlowCancelled(_flow: UfFlowDto): void {
        this.ufFlowObjectIdentifier = null;
    }
}
